import React, { useEffect } from "react";
import {
  calculatePaginationOffset,
  getFirmIdFromUrl,
  getFirmProfileData,
} from "../../../src/utils/firmPageUtils";
import { FIRM_PAGINATION_REVIEWS_PER_PAGE } from "../../../src/components/Firm/components/Reviews/ReviewsList";
import * as analytics from "../../../src/analytics";
import {
  API_KEYS,
  LAST_EVENT_FIRED_FOR_FIRM_STATS,
  LAST_PROFILE_VIEWED_FIRM_ID,
  GA_FIRM_SESSION_ID,
  LIVE_SITE_URL,
  POPULAR_FIRM_URLS
} from "../../../src/__constants__";
import _ from "lodash";
import axios from "axios";
import { withPermalinkRedirection } from "../../../src/components/HigherOrderComponents/PermalinkRedirection";
import {
  getOrderedServicesFromIds,
  getVerticalIdFromFirmType,
} from "../../../src/data/attributeHelper";
import { redirect } from "../../../src/utils/urlHelpers";
import FirmPage from "../../../src/components/Firm/FirmPage";
import {LocalBusinessJsonLd, NextSeo} from 'next-seo';
import { isProductionEnv } from "../../../src/utils/utilities";

const FirmPageWithRedirect = withPermalinkRedirection(FirmPage, "firm");

const setLastFirmEventFired = (event, firmId) => {
  localStorage.setItem(LAST_EVENT_FIRED_FOR_FIRM_STATS, event);
  localStorage.setItem(LAST_PROFILE_VIEWED_FIRM_ID, firmId);
};

const fireUniquePageViewEvent = (firmId) => {
  analytics.track("vf_firm_profile_unique_page_view", {
    firmId,
  });
  setLastFirmEventFired(
    "vf_firm_profile_unique_page_view",
    firmId
  );
};

const FirmProfilePage = (props) => {

  useEffect(() => {
    const lastEventFiredForFirmStats = localStorage.getItem(LAST_EVENT_FIRED_FOR_FIRM_STATS);
    const firmId = localStorage.getItem(LAST_PROFILE_VIEWED_FIRM_ID);
    const localStorageGaSessionId = localStorage.getItem(GA_FIRM_SESSION_ID);

    analytics.getField("session_id", (sessionId) => {
      if (localStorageGaSessionId !== sessionId) {
        analytics.track("vf_first_firm_profile_view_in_session", {
          firmId: props.firmId,
        });
        localStorage.setItem(GA_FIRM_SESSION_ID, sessionId);
      }
    });

    if (
      lastEventFiredForFirmStats !== "vf_firm_profile_unique_page_view" ||
      firmId !== props.firmId.toString()
    ) {
      fireUniquePageViewEvent(props.firmId);
    } else {
      analytics.getField("session_id", (sessionId) => {
        if (localStorageGaSessionId !== sessionId) {
          fireUniquePageViewEvent(props.firmId);
        }
      });
    }

    const category = props.type === "firm" ? "Firm Profile" : "Network Profile";
    analytics.track("Visit", { category });
  }, []);

  const {
    reviewAverage,
    reviewCount,
    verifiedCount,
    name,
    about,
    url,
    addressLine1,
    town,
    postcode
  } = getFirmProfileData(props.profile)

  const metaDescCoreContent = `${name}: ${reviewAverage} / 5 from ${reviewCount} reviews of ${verifiedCount} advisers.`
  const metaDescTruncationLimit = 119
  const metaDescTrimmed = `${metaDescCoreContent} ${about?.substring(0, metaDescTruncationLimit - metaDescCoreContent.length)}`

  const rating = reviewCount > 0 ?
      {
        ratingValue: reviewAverage,
        ratingCount: reviewCount,
      } : null
  
  const shouldBotsIndexAndFollow = isProductionEnv()

  return (
    <>
      <NextSeo
        description={metaDescTrimmed}
        nofollow={!shouldBotsIndexAndFollow}
        noindex={!shouldBotsIndexAndFollow}
      />
      <LocalBusinessJsonLd
          address={{
            streetAddress: addressLine1,
            addressLocality: town,
            postalCode: postcode,
            addressCountry: 'UK',
          }}
          description={metaDescTrimmed}
          id={LIVE_SITE_URL+url}
          name={name}
          type={'FinancialService'}
          rating={rating}
      />
      <FirmPageWithRedirect
        services={props.services}
        reviewFilters={props.reviewFilters}
        profile={props.profile}
        key={`firm-${new Date().toISOString()}`}
        firmPermalink={props.firmPermalink}
        firmOrNetworkType={props.type}
        firmId={props.firmId}
        advisers={props.advisers}
        awards={props.awards}
        accreditations={props.accreditations}
        initialReview={props.initialReview}
        initialPage={props.initialPage}
        unclaimed={props.unclaimed}
      />
    </>
)};

export async function getStaticPaths() {
  const paths = POPULAR_FIRM_URLS.map(url => ({ params: { firmPermalink: url } }))

  return {
    paths,
    fallback: 'blocking'
  }
}

export async function getStaticProps(props) {
  const { params, query } = props;
  const id = getFirmIdFromUrl(params.firmPermalink);
  let profile = null;
  try {
    const profileRes = await axios.get(
      `${API_KEYS.API_URL_INTERNAL}/v2/public/firm/${id}`
    );
    profile = _.get(profileRes, "data.data");
  } catch {
    return redirect();
  }

  const professionalsRes = await axios.get(
    `${API_KEYS.API_URL_INTERNAL}/v2/public/firm/${id}/professionals?limit=6&offset=0`
  );
  const advisers = _.get(professionalsRes, "data");

  const offset = calculatePaginationOffset(query);

  const [reviewsRes, reviewFiltersRes, servicesRes, awardsRes, accreditationRes] = await Promise.all(
    [axios.get(`${API_KEYS.API_URL_INTERNAL}/v2/public/firm/${id}/reviews?limit=${FIRM_PAGINATION_REVIEWS_PER_PAGE}&offset=${offset}`),
    axios.get(`${API_KEYS.API_URL_INTERNAL}/v2/public/firm/${id}/review-filters`),
    axios.get(`${API_KEYS.API_URL_INTERNAL}/v2/public/firm/${id}/services`),
    axios.get(`${API_KEYS.API_URL_INTERNAL}/v2/public/firm/${id}/awards`),
    axios.get(`${API_KEYS.API_URL_INTERNAL}/v2/public/firm/${id}/accreditations`)
  ]);

  const initialReview = _.get(reviewsRes, "data");
  const reviewFilters = _.get(reviewFiltersRes, "data");
  const firmType = _.get(servicesRes,"data.aggregate_data.firm_type","adviser");
  const serviceIds = _.get(servicesRes, "data.data", []);
  const verticalId = getVerticalIdFromFirmType(firmType);
  const services = getOrderedServicesFromIds(serviceIds, verticalId);
  const awards = _.get(awardsRes, "data.data");
  const accreditations = _.get(accreditationRes, "data.data");

  return {
    props: {
      profile: profile,
      type: "firm",
      firmId: id,
      initialReview: initialReview,
      firmPermalink: params.firmPermalink,
      services: services,
      reviewFilters: reviewFilters.data,
      advisers: advisers,
      awards: awards,
      accreditations: accreditations,
      initialPage: query?.page ? query.page : 1,
      unclaimed: !profile.claimed_at,
    },
    revalidate: 7200 // 2 hours
  };
}

export default FirmProfilePage;
